import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertCircle, 
  AlertTriangle, 
  XCircle, 
  Info,
  X,
  Copy,
  Mail,
  RefreshCw
} from 'lucide-react';

/**
 * Premium Error Popup Component
 * 
 * A sophisticated error display component with:
 * - Error code display
 * - Categorized error types
 * - Premium design with gradients
 * - Animation support
 * - Copy error code functionality
 * - Contact support action
 * - Retry functionality
 */

const ErrorPopup = ({ 
  error, 
  onClose, 
  onRetry,
  autoClose = false,
  autoCloseDuration = 5000,
  showRetry = false,
  showContact = true
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (autoClose && error) {
      const timer = setTimeout(() => {
        onClose && onClose();
      }, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDuration, error, onClose]);

  if (!error) return null;

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'error':
        return {
          icon: XCircle,
          iconColor: 'text-red-500',
          bgGradient: 'from-red-50 to-red-100',
          borderColor: 'border-red-200',
          accentColor: 'bg-red-500'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconColor: 'text-yellow-500',
          bgGradient: 'from-yellow-50 to-yellow-100',
          borderColor: 'border-yellow-200',
          accentColor: 'bg-yellow-500'
        };
      case 'info':
        return {
          icon: Info,
          iconColor: 'text-blue-500',
          bgGradient: 'from-blue-50 to-blue-100',
          borderColor: 'border-blue-200',
          accentColor: 'bg-blue-500'
        };
      default:
        return {
          icon: AlertCircle,
          iconColor: 'text-gray-500',
          bgGradient: 'from-gray-50 to-gray-100',
          borderColor: 'border-gray-200',
          accentColor: 'bg-gray-500'
        };
    }
  };

  const config = getSeverityConfig(error.severity || 'error');
  const Icon = config.icon;

  const copyErrorCode = () => {
    if (error.code) {
      navigator.clipboard.writeText(error.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleContactSupport = () => {
    const subject = encodeURIComponent(`Support Request - Error ${error.code}`);
    const body = encodeURIComponent(
      `Error Code: ${error.code}\n` +
      `Title: ${error.title}\n` +
      `Timestamp: ${error.timestamp || new Date().toISOString()}\n` +
      `\nPlease describe the issue:\n\n`
    );
    window.location.href = `mailto:support@flexcoa.com?subject=${subject}&body=${body}`;
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const popupVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      y: 20,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <AnimatePresence>
      {error && (
        <motion.div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-5"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div 
            className="relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl dark:shadow-gray-900/20 max-w-[540px] w-full overflow-hidden border-t-4 border-[#007077]"
            variants={popupVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Close Button */}
            {onClose && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 bg-black/5 hover:bg-black/10 border-none rounded-lg w-9 h-9 flex items-center justify-center cursor-pointer transition-all duration-200 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 z-10 hover:scale-105 active:scale-95"
                aria-label="Close error popup"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            {/* Header with Icon and Error Code */}
            <div className="pt-10 px-8 pb-6 text-center relative">
              <div className={`relative w-[88px] h-[88px] rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg dark:shadow-gray-900/20 ${config.accentColor}`}>
                <Icon className="w-12 h-12 text-white relative z-10" />
                <div className="absolute inset-0 rounded-full bg-current opacity-40 animate-ping" style={{ animationDuration: '2s' }} />
              </div>
              
              {error.code && (
                <div className="inline-block bg-gradient-to-br from-gray-50 to-gray-200 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl py-3 px-5 shadow-md dark:shadow-gray-900/20">
                  <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1">
                    Error Code
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg font-bold text-gray-900 dark:text-gray-100 tracking-wide">
                      {error.code}
                    </span>
                    <button
                      onClick={copyErrorCode}
                      className="bg-[#007077]/10 hover:bg-[#007077]/20 border-none rounded-md p-1.5 cursor-pointer transition-all duration-200 text-[#007077] flex items-center justify-center hover:scale-105 active:scale-95"
                      title="Copy error code"
                      aria-label="Copy error code"
                    >
                      {copied ? (
                        <span className="text-[11px] font-semibold text-[#007077] px-1">
                          Copied!
                        </span>
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-8 pb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3 text-center font-mulish">
                {error.title || 'An Error Occurred'}
              </h2>
              
              <p className="text-base text-gray-700 dark:text-gray-200 leading-relaxed text-center mb-2 font-mulish">
                {error.userMessage || error.message || 'An unexpected error occurred. Please try again or contact support.'}
              </p>

              {error.timestamp && (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                  {new Date(error.timestamp).toLocaleString()}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="px-8 pb-6 flex flex-col sm:flex-row gap-3 justify-center">
              {showRetry && onRetry && (
                <button
                  onClick={onRetry}
                  className="flex items-center justify-center bg-gradient-to-b from-[#007077] to-[#05574A] text-white px-6 py-3 rounded-lg font-mulish font-bold text-base hover:opacity-90 transition-all duration-200 shadow-md dark:shadow-gray-900/20 hover:shadow-lg hover:scale-105 active:scale-95"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>
              )}
              
              {showContact && (
                <button
                  onClick={handleContactSupport}
                  className="flex items-center justify-center bg-white dark:bg-gray-800 border-2 border-[#007077] text-[#007077] px-6 py-3 rounded-lg font-mulish font-bold text-base hover:bg-[#007077] hover:text-white transition-all duration-200 shadow-sm dark:shadow-gray-900/20 hover:shadow-md hover:scale-105 active:scale-95"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </button>
              )}

              {onClose && !showRetry && (
                <button
                  onClick={onClose}
                  className="flex items-center justify-center bg-gradient-to-b from-[#007077] to-[#05574A] text-white px-6 py-3 rounded-lg font-mulish font-bold text-base hover:opacity-90 transition-all duration-200 shadow-md dark:shadow-gray-900/20 hover:shadow-lg hover:scale-105 active:scale-95"
                >
                  Close
                </button>
              )}
            </div>

            {/* Support Message */}
            <div className="px-8 pb-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                If this problem persists, please contact support with the error code above.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ErrorPopup;
